import React from 'react';

import {useQueryGraphProps} from './results-graph/use-query-graph-props';
import {Graph} from '../../../../components/graph/components/graph';
import {BrowserNotSupportedView} from '../../../../components/graph/components/browser-not-supported-view';
import {isGraphUnmanageable} from './results-graph/helpers';
import {GraphLoadingComponent} from '../../../../components/graph/components/graph-loading-component';
import {usePathsResultListProps} from './right-panel/paths-result-list/use-paths-result-list-props';
import {useGraphSelectionProps} from '../../../../components/graph/graph-logic/use-graph-selection-props';
import {MaxPathLengthOptionResult} from './results-graph/requests/use-query-result-max-length-options';
import {GraphDisplayErr} from '../../../../components/graph/components/graph-display-err';
import {useIsCantDisplayGraph} from './results-graph/use-is-cant-display-graph';

interface Props {
    graphProps: ReturnType<typeof useQueryGraphProps>;
    resultsListProps: ReturnType<typeof usePathsResultListProps>;
    selectionProps: ReturnType<typeof useGraphSelectionProps>;
    maxPathLengthOptions: MaxPathLengthOptionResult;
    isCantDisplayGraphProps: ReturnType<typeof useIsCantDisplayGraph>;
}

export function QueryResults(props: Props) {
    const {isQueryResultsUnmanageable, isResultIncomplete} = props.isCantDisplayGraphProps;

    if (!props.graphProps.queryGraphRequest.data || !props.graphProps.structuredGraph) {
        return null;
    }

    if (isQueryResultsUnmanageable) {
        return (
            <GraphDisplayErr label="The graph is too large to be displayed. Try again with a more concrete query." />
        );
    }

    if (isResultIncomplete) {
        if (props.resultsListProps.queryResultsListItems?.length) {
            return (
                <GraphDisplayErr label="The graph is not displayed because it has no edges. You can view results as a list in right panel." />
            );
        }

        return (
            <GraphDisplayErr label="This query has no results. The graph cannot be displayed." />
        );
    }

    const selectedMaxPathLengthOption = props.maxPathLengthOptions[
        props.graphProps.graphSettings.viewKind
    ].find((option) => option.pathLength === props.graphProps.graphSettings.maxPathLength)!;
    const isCurrentGraphUnmanageable = isGraphUnmanageable(
        selectedMaxPathLengthOption.amountOfNodes
    );

    if (isCurrentGraphUnmanageable) {
        return (
            <GraphDisplayErr label="The graph is too large to be displayed. Select the lower value in the Max Path Length field." />
        );
    }

    if (props.graphProps.layoutAndCameraProps.isBrowserUnsupported) {
        return <BrowserNotSupportedView />;
    }

    const isAllNodesInvisible =
        !props.graphProps.hiddenNodesProps.isHiddenNodesVisible &&
        props.graphProps.queryGraphRequest.data.nodes.every((node) =>
            props.graphProps.hiddenNodesProps.hiddenNodes.includes(node.id)
        );
    if (!props.graphProps.layoutAndCameraProps.visibleNodesPositions && !isAllNodesInvisible) {
        return <GraphLoadingComponent />;
    }

    return (
        <Graph
            nodes={props.graphProps.queryGraphRequest.data.nodes}
            edges={props.graphProps.queryGraphRequest.data.edges}
            selectNodes={props.graphProps.graphActions.nodeSelectionChangedAction}
            selectedNodes={props.selectionProps.selectedNodes}
            hoveredNodes={props.selectionProps.hoveredNodes}
            setHoveredNodes={props.selectionProps.setHoveredNodes}
            nodePositions={{
                ...props.graphProps.layoutAndCameraProps.visibleNodesPositions,
                ...props.graphProps.layoutAndCameraProps.invisibleNodesPositions
            }}
            handleNodeMoved={props.graphProps.graphActions.nodeMovedAction}
            zoom={props.graphProps.layoutAndCameraProps.zoom}
            setZoom={props.graphProps.layoutAndCameraProps.setZoom}
            pan={props.graphProps.layoutAndCameraProps.pan}
            setPan={props.graphProps.layoutAndCameraProps.setPan}
            key={`${props.graphProps.layoutAndCameraProps.layoutAlgorithm}${props.graphProps.graphSettings.maxPathLength}`}
            changesClearedOrSavedCounter={props.graphProps.changesClearedOrSavedCounter}
            hiddenNodesActions={props.graphProps.hiddenNodesActions}
            hiddenNodes={props.graphProps.hiddenNodesProps.hiddenNodes}
            undoRedoProps={props.graphProps.undoRedoProps}
        />
    );
}
